import { z } from '@uturn/portal/zod';
import parsePhoneNumber, {
	type CountryCode,
	isSupportedCountry,
} from 'libphonenumber-js/max';
import {
	addIssueCustom,
	addIssueRequired,
	formValidationMessage,
} from '../utils';

const DEFAULT_COUNTRY_CODE: CountryCode = 'NL';

/** @see PhoneDto */
const phoneSchema = z.object({
	number: z.string(),
	countryCode: z.string(),
	/** Extra prop. for Contact, Applicable hours, Transport type, etc. */
	comment: z.string().optional(),
});

type Phone = z.infer<typeof phoneSchema>;

const phoneInputSchema = z.union([z.string(), phoneSchema]);

type PhoneInput = z.infer<typeof phoneInputSchema>;

const isPhone = (value: unknown): value is Phone => {
	return phoneSchema.safeParse(value).success;
};

export const getValue = (value?: PhoneInput) => {
	if (!isPhone(value)) {
		return value;
	}

	const countryCode = isSupportedCountry(value.countryCode)
		? value.countryCode
		: DEFAULT_COUNTRY_CODE;

	const parser = parsePhoneNumber(value.number, {
		defaultCountry: countryCode,
	});

	if (!parser || !parser.isValid() || !parser.number) {
		return value.number;
	}

	const number = parser.format('E.164');
	if (!value.comment) {
		return number;
	}

	return `${number} (${value.comment})`;
};

/** Match pattern: phone number followed by space and comment in parentheses. */
const parsePhoneWithComment = (input: string): Phone => {
	const match = input.match(/^(.+?)\s+\(([^)]+)\)$/);
	if (match) {
		return {
			countryCode: DEFAULT_COUNTRY_CODE,
			number: match[1].trim(),
			comment: match[2],
		};
	}

	return {
		countryCode: DEFAULT_COUNTRY_CODE,
		number: input.trim(),
	};
};

export const schema = phoneInputSchema.transform((value, ctx) => {
	if (!isPhone(value) && value === '') {
		addIssueRequired(ctx);
		return z.NEVER;
	}

	const isObject = isPhone(value);
	const { number, countryCode, comment } =
		typeof value === 'string' ? parsePhoneWithComment(value) : value;

	let input = number;

	/**
	 * Transform 00 numbers into + numbers.
	 */
	if (input.startsWith('00')) {
		input = input.replace(/^0{2}/, '+');
	}

	/**
	 * Require E.164 +prefix for string inputs (typed by user).
	 * Phone objects from BE may store national format and rely on
	 * `countryCode` — let `parsePhoneNumber` below derive the prefix
	 * from `defaultCountry`.
	 */
	if (!isObject && !input.match(/^\+\d+/)) {
		addIssueCustom(ctx, 'phone_e164_invalid');
		return z.NEVER;
	}

	/**
	 * Check if input has comment outside round brackets.
	 */
	if (input.match(/\s+[a-zA-Z]/)) {
		addIssueCustom(ctx, 'phone_comment_outside_round_brackets');
		return z.NEVER;
	}

	/**
	 * Parse phone number and determine country code from leading country calling code
	 * during validation.
	 */
	const parser = parsePhoneNumber(input, {
		defaultCountry: isSupportedCountry(countryCode)
			? countryCode
			: DEFAULT_COUNTRY_CODE,
	});

	if (!parser || !parser.isValid() || !parser.country || !parser.number) {
		addIssueCustom(ctx, 'phone_invalid');
		return z.NEVER;
	}

	const phone: Phone = {
		countryCode: parser.country,
		number: parser.number,
		comment,
	};

	return phone;
});

/** Check for and extract country code prefix like "NL:", "BE:" as a comment. */
const getCountryCodePrefix = (value: string) => {
	const countryPrefixMatch = value.match(/^([A-Z]{2}):\s*/);
	if (countryPrefixMatch) {
		const countryPrefix = countryPrefixMatch[1];

		return {
			number: value.replace(countryPrefixMatch[0], ''),
			countryCode: isSupportedCountry(countryPrefix)
				? countryPrefix
				: undefined,
		};
	}

	return undefined;
};

/** Check if there's a comment (alphabetic text or parentheses with text). */
const getCommentSuffix = (value: string) => {
	const commentMatch = value.match(/(\s+[a-zA-Z]|\s*\([a-zA-Z])/);
	if (commentMatch && commentMatch.index !== undefined) {
		const comment = value
			.substring(commentMatch.index)
			.trim()
			.replace(/^\((.+)\)$/, '$1');
		return {
			number: value.substring(0, commentMatch.index),
			comment: comment !== '' ? comment : undefined,
		};
	}

	return undefined;
};

/** Check for extensions (dash + 1-3 digits at the end). */
const getExtensionSuffix = (value: string) => {
	const extensionMatch = value.match(/(-\d{1,3})$/);
	if (extensionMatch) {
		const extension = extensionMatch[1];
		return {
			number: value
				.substring(0, value.length - extension.length)
				.replace(/[-–]/g, ''),
			comment: `x${extension.substring(1)}`,
		};
	}

	return undefined;
};

/** Clean the phone number part. */
const cleanNumber = (value: string) => {
	let result = value.replace(/\s/g, '');
	result = result.replace(/\(0\)/g, '');
	result = result.replace(/[^0-9+]/g, '');

	return result;
};

export const splitPhone = (phone: Phone): Phone[] => {
	// Handle email addresses - return as-is
	if (phone.number.includes('@')) {
		return [phone];
	}

	// Get default country code - use input's country code throughout
	const defaultCountryCode = isSupportedCountry(phone.countryCode)
		? phone.countryCode
		: DEFAULT_COUNTRY_CODE;

	// Split by common delimiters: //, /, &, or ,
	const phones = phone.number.split(/\/\/|\/|&|,/).map((phoneNr) => {
		let number = phoneNr.trim();
		let countryCode = defaultCountryCode;
		let comments: string[] = [];

		const foundCountryCodePrefix = getCountryCodePrefix(number);
		if (foundCountryCodePrefix) {
			number = foundCountryCodePrefix.number;
			if (foundCountryCodePrefix.countryCode) {
				countryCode = foundCountryCodePrefix.countryCode;
			}
		}

		const foundCommentSuffix = getCommentSuffix(number);
		if (foundCommentSuffix) {
			number = foundCommentSuffix.number;
			if (foundCommentSuffix.comment) {
				comments.push(foundCommentSuffix.comment);
			}
		}

		const foundExtensionSuffix = getExtensionSuffix(number);
		if (foundExtensionSuffix) {
			number = foundExtensionSuffix.number;
			if (foundExtensionSuffix.comment) {
				comments.push(foundExtensionSuffix.comment);
			}
		}

		number = cleanNumber(number);

		// If cleaned phone phoneNr doesn't contain digits, it's not a phone - preserve original
		if (!/\d/.test(number)) {
			countryCode = defaultCountryCode;
			number = phoneNr;
			comments = [];
		}

		const result: Phone = {
			countryCode,
			number,
			comment: comments.length > 0 ? comments.join(', ') : undefined,
		};

		return result;
	});

	// Filter out empty phone phones
	return phones.filter((phone) => phone.number.length > 0);
};

export const split = (phone?: Phone): Phone[] => {
	if (!phone) {
		return [];
	}

	return splitPhone(phone);
};

export const LIST_SEPARATOR = ', ';

export const join = (phones: Phone[]): Phone => ({
	countryCode: phones.length > 0 ? phones[0].countryCode : DEFAULT_COUNTRY_CODE,
	number: phones.map((phone) => getValue(phone)).join(LIST_SEPARATOR),
});

/** Straight zod schema. */
export const bareSchema = phoneSchema;

/** Phone. */
export type Type = Phone;

/** Argument satisfies the Phone type. */
export const isType = isPhone;

/** Maximum E.164 format length accroding to [Wikipedia](https://en.wikipedia.org/wiki/E.164). */
export const MAX_LENGTH = 15;
